import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, X, Save, Loader2, Upload, FileText } from "lucide-react";
import { admin, fileUrl } from "@/lib/api";
import { Field, TabHeader, Badge, inputCls, textareaCls } from "@/admin/ui";

const EMPTY = {
  title: "", description: "", category: "", tagsText: "", tier: "free",
  file: "", thumbnail: "", icon: "FileText", published: true,
};

const ICON_OPTIONS = ["Sun", "LayoutList", "Coins", "NotebookPen", "BookOpen", "Library", "FileText"];

function UploadButton({ label, accept, onDone, testId }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const { url } = await admin.upload(f);
      onDone(url);
      toast.success("Uploaded");
    } catch {
      toast.error("Upload failed — only PDF, PNG, JPG, WEBP allowed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={pick} />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid={testId} className="btn-secondary !px-4 !py-2 !text-xs disabled:opacity-60">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
        {busy ? "Uploading…" : label}
      </button>
    </>
  );
}

export default function ResourcesTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => admin.list("resources").then(setItems).catch(() => toast.error("Could not load resources"));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.file) return toast.error("Please upload a PDF file");
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      };
      delete payload.tagsText;
      if (form.id) await admin.update("resources", form.id, payload);
      else await admin.create("resources", payload);
      toast.success(form.id ? "Resource updated" : "Resource added");
      setForm(null);
      load();
    } catch {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await admin.remove("resources", r.id);
    toast.success("Resource deleted");
    load();
  };

  if (form) {
    return (
      <div data-testid="admin-resource-form">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-semibold text-foreground">
            {form.id ? "Edit Resource" : "New Resource"}
          </h1>
          <button onClick={() => setForm(null)} data-testid="admin-resource-cancel" className="btn-secondary !px-4 !py-2 !text-xs">
            <X className="h-3.5 w-3.5" /> Cancel
          </button>
        </div>
        <div className="mt-8 grid gap-5 rounded-3xl border border-border bg-card p-7 shadow-soft sm:grid-cols-2">
          <Field label="Title"><input className={inputCls} value={form.title} onChange={set("title")} data-testid="resource-title-input" /></Field>
          <Field label="Category" hint="Reuse an existing category or type a new one — it appears as a filter on the Resources page.">
            <input className={inputCls} value={form.category} onChange={set("category")} list="resource-categories" data-testid="resource-category-input" />
            <datalist id="resource-categories">
              {[...new Set((items || []).map((r) => r.category))].map((c) => <option key={c} value={c} />)}
            </datalist>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Description"><textarea className={textareaCls} rows={2} value={form.description} onChange={set("description")} data-testid="resource-description-input" /></Field>
          </div>
          <Field label="Tags" hint="Comma separated, e.g. routines, mornings, visual supports.">
            <input className={inputCls} value={form.tagsText} onChange={set("tagsText")} data-testid="resource-tags-input" />
          </Field>
          <Field label="Tier">
            <select className={inputCls} value={form.tier} onChange={set("tier")} data-testid="resource-tier-select">
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </select>
          </Field>
          <Field label="Icon">
            <select className={inputCls} value={form.icon} onChange={set("icon")} data-testid="resource-icon-select">
              {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="PDF file" hint={form.file ? `Current: ${form.file.split("/").pop()}` : "Upload the downloadable PDF."}>
            <div className="flex items-center gap-3">
              <UploadButton label={form.file ? "Replace PDF" : "Upload PDF"} accept="application/pdf" onDone={(url) => setForm((f) => ({ ...f, file: url }))} testId="resource-file-upload" />
              {form.file && <span className="flex items-center gap-1.5 text-xs text-forest dark:text-sage"><FileText className="h-3.5 w-3.5" /> Attached</span>}
            </div>
          </Field>
          <Field label="Thumbnail image" hint="Optional — shown on the resource card.">
            <div className="flex items-center gap-3">
              <UploadButton label={form.thumbnail ? "Replace image" : "Upload image"} accept="image/png,image/jpeg,image/webp" onDone={(url) => setForm((f) => ({ ...f, thumbnail: url }))} testId="resource-thumbnail-upload" />
              {form.thumbnail && <img src={fileUrl(form.thumbnail)} alt="" className="h-10 w-14 rounded-lg object-cover" />}
            </div>
          </Field>
          <Field label="Visibility">
            <label className="flex h-11 items-center gap-2.5 text-sm text-foreground">
              <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} data-testid="resource-published-checkbox" className="h-4 w-4 accent-terracotta-500" />
              Visible in the Resource Library
            </label>
          </Field>
        </div>
        <button onClick={save} disabled={saving} data-testid="admin-resource-save" className="btn-primary mt-6 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save Resource"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="admin-resources-tab">
      <TabHeader
        title="Resources"
        description="Upload PDFs, checklists, and visual supports. Create categories and tags, and mark items Free or Premium."
        actionLabel="New Resource"
        onAdd={() => setForm({ ...EMPTY })}
        testId="admin-add-resource"
      />
      <div className="mt-8 space-y-3">
        {(items || []).map((r) => (
          <div key={r.id} data-testid={`admin-resource-row-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft">
            {r.thumbnail && <img src={fileUrl(r.thumbnail)} alt="" className="h-10 w-14 rounded-lg object-cover" />}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{r.title}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {r.category}{(r.tags || []).length > 0 ? ` · ${r.tags.join(", ")}` : ""}
              </p>
            </div>
            <Badge tone={r.tier === "premium" ? "terracotta" : "forest"}>{r.tier}</Badge>
            {!r.published && <Badge>hidden</Badge>}
            <button onClick={() => setForm({ ...EMPTY, ...r, tagsText: (r.tags || []).join(", ") })} data-testid={`admin-resource-edit-${r.id}`} aria-label={`Edit ${r.title}`} className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-terracotta-500 hover:text-terracotta-500">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => remove(r)} data-testid={`admin-resource-delete-${r.id}`} aria-label={`Delete ${r.title}`} className="rounded-full border border-border p-2 text-muted-foreground transition-colors duration-300 hover:border-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {items && items.length === 0 && (
          <p className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No resources yet — upload your first one.</p>
        )}
      </div>
    </div>
  );
}
